/**
 * The settings map, shared by both breakpoints.
 *
 * Native gives every settings row its own stack screen (`SettingsScreen` →
 * `Account`, `LanguageSettings`, `Permissions`, `TutorialsSettings`,
 * `SignOutSettings`, `SettingsLink`). The web keeps that shape rather than
 * inventing a second one: each of those screens is a path here, so a phone
 * drills in and back exactly as native does, and a desktop can link straight
 * to a panel.
 *
 * `group` is the section heading the row sits under on the hub — native's
 * `TableSectionTitle` grouping, in native's order.
 */
import {
  helpCircleOutline,
  keyOutline,
  languageOutline,
  logOutOutline,
  personCircleOutline,
  schoolOutline,
  shieldCheckmarkOutline,
} from 'ionicons/icons';

export const SETTINGS_SECTIONS = [
  'profile',
  'language',
  'permissions',
  'tutorials',
  'about',
  'legal',
  'signout',
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export type SettingsGroup = 'account' | 'preferences' | 'about' | 'legal' | 'advanced';

export interface SettingsSectionDef {
  key: SettingsSection;
  group: SettingsGroup;
  icon: string;
  /** Namespaced key for the row label and the sub-page title. */
  titleKey: string;
  /** Namespaced key for the row's second line, as native's `value`. */
  valueKey?: string;
  /**
   * Rows the hub renders as direct external links instead of a drill-down:
   * native shows an intermediary screen with a button because a phone cannot
   * open a browser in place, which is not a constraint here. The panel still
   * exists for the desktop sub-menu and for a direct link.
   */
  externalOnHub?: boolean;
  destructive?: boolean;
}

export const SETTINGS_SECTION_DEFS: SettingsSectionDef[] = [
  {
    key: 'profile',
    group: 'account',
    icon: personCircleOutline,
    titleKey: 'settings:profile.title',
  },
  {
    key: 'language',
    group: 'preferences',
    icon: languageOutline,
    titleKey: 'settings:language.title',
  },
  {
    key: 'permissions',
    group: 'preferences',
    icon: keyOutline,
    titleKey: 'settings:permissions.title',
    valueKey: 'settings:permissions.subtitle',
  },
  {
    key: 'tutorials',
    group: 'preferences',
    icon: schoolOutline,
    titleKey: 'settings:tutorials.label',
    valueKey: 'settings:tutorials.value',
  },
  {
    key: 'about',
    group: 'about',
    icon: helpCircleOutline,
    titleKey: 'settings:sections.about',
    externalOnHub: true,
  },
  {
    key: 'legal',
    group: 'legal',
    icon: shieldCheckmarkOutline,
    titleKey: 'web:settings.legalSection',
    externalOnHub: true,
  },
  {
    key: 'signout',
    group: 'advanced',
    icon: logOutOutline,
    titleKey: 'settings:signOut.label',
    valueKey: 'settings:signOut.value',
    destructive: true,
  },
];

/** Heading key per group, in the order the hub and the sub-menu render them. */
export const SETTINGS_GROUPS: Array<{ key: SettingsGroup; titleKey: string }> = [
  { key: 'account', titleKey: 'settings:sections.account' },
  { key: 'preferences', titleKey: 'settings:sections.preferences' },
  { key: 'about', titleKey: 'settings:sections.about' },
  { key: 'legal', titleKey: 'web:settings.legalSection' },
  { key: 'advanced', titleKey: 'settings:sections.advanced' },
];

export const SETTINGS_PATH = '/settings';

export function settingsPath(section?: SettingsSection | null): string {
  return section ? `${SETTINGS_PATH}/${section}` : SETTINGS_PATH;
}

export function settingsSectionDef(section: SettingsSection): SettingsSectionDef {
  const def = SETTINGS_SECTION_DEFS.find((entry) => entry.key === section);
  if (!def) throw new Error(`Unknown settings section: ${section}`);
  return def;
}
